import { Bars3Icon, ChevronDoubleLeftIcon } from '@heroicons/react/24/outline';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RichTextEditor } from '../../components/editor/RichTextEditor';
import { DocumentationHtmlContent } from '../../components/editor/DocumentationHtmlContent';
import { DocumentationMarkdownEditor } from '../../components/editor/DocumentationMarkdownEditor';
import { DocumentationMarkdownContent } from '../../components/editor/DocumentationMarkdownContent';
import { ColumnResizeHandle } from '../../components/ui/ColumnResizeHandle';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useResizablePanelWidth } from '../../hooks/useResizablePanelWidth';
import {
  beginEditDocumentationPage,
  createDocumentationPage,
  deleteDocumentationPage,
  DocumentationContentFormat,
  DocumentationPage,
  DocumentationPageStatus,
  DocumentationPageTreeNode,
  DocumentationPageUpsert,
  DocumentationPageVersionSummary,
  DocumentationSpace,
  DocumentationVisibility,
  getDocumentationPage,
  getDocumentationPageTree,
  getDocumentationSpace,
  listDocumentationPageVersions,
  publishDocumentationPage,
  restoreDocumentationPageVersion,
  updateDocumentationPage,
  updateDocumentationPageMetadata,
} from '../../services/documentationApi';
import { DocumentationCreatePageModal } from './DocumentationCreatePageModal';
import { DocumentationDetailsDraft, DocumentationDetailsPanel } from './DocumentationDetailsPanel';
import { DocumentationMovePageModal } from './DocumentationMovePageModal';
import { DocumentationPublishModal, toDatetimeLocalValue } from './DocumentationPublishModal';
import { DocumentationVersionsModal } from './DocumentationVersionsModal';
import {
  DocumentationPageTree,
  DOCUMENTATION_TREE_ROOT_ID,
  DocumentationTreeMenuAction,
  TreeDropPosition,
} from './DocumentationPageTree';
import { AutosaveIndicator, DocumentationPageToolbar } from './DocumentationPageToolbar';
import './documentationWiki.css';

import { useDocumentationShell } from './documentationShell';

const AUTOSAVE_MS = 1500;
const DOCS_TREE_DEFAULT_WIDTH = 260;
const DOCS_TREE_MIN_WIDTH = 180;
const DOCS_TREE_MAX_WIDTH = 480;
const DOCS_TREE_COLLAPSED_WIDTH = 52;

function isMarkdownFormat(format?: string | null): boolean {
  return (format ?? '').toLowerCase() === 'markdown';
}

function defaultBodyForFormat(format: DocumentationContentFormat): string {
  return format === 'Html' ? '<p></p>' : '';
}

function readTreeCollapsed(storageKey: string): boolean {
  try {
    return window.localStorage.getItem(storageKey) === '1';
  } catch {
    return false;
  }
}

function flattenTree(nodes: DocumentationPageTreeNode[]): DocumentationPageTreeNode[] {
  const result: DocumentationPageTreeNode[] = [];
  for (const node of nodes) {
    result.push(node);
    if (node.children?.length) {
      result.push(...flattenTree(node.children));
    }
  }
  return result;
}

function findNode(
  nodes: DocumentationPageTreeNode[],
  id: string,
): DocumentationPageTreeNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const found = findNode(node.children ?? [], id);
    if (found) return found;
  }
  return null;
}

function getParentId(nodes: DocumentationPageTreeNode[], id: string): string | null {
  for (const node of nodes) {
    if ((node.children ?? []).some((c) => c.id === id)) return node.id;
    const nested = getParentId(node.children ?? [], id);
    if (nested) return nested;
  }
  return null;
}

function getSiblings(
  tree: DocumentationPageTreeNode[],
  parentId: string | null,
): DocumentationPageTreeNode[] {
  if (!parentId) return tree;
  const parent = findNode(tree, parentId);
  return parent?.children ?? [];
}

function parseTags(tagInput: string): string[] {
  return tagInput
    .split(',')
    .map((t) => t.trim())
    .filter(Boolean);
}

function detailsFromPage(page: DocumentationPage): DocumentationDetailsDraft {
  return {
    visibility: (page.visibility as DocumentationVisibility) || 'Private',
    tagInput: (page.tags ?? []).join(', '),
    previousPageId: page.previousPageId ?? '',
    nextPageId: page.nextPageId ?? '',
  };
}

function findBreadcrumbPath(
  nodes: DocumentationPageTreeNode[],
  pageId: string,
  trail: DocumentationPageTreeNode[] = [],
): DocumentationPageTreeNode[] | null {
  for (const node of nodes) {
    const next = [...trail, node];
    if (node.id === pageId) return next;
    const found = findBreadcrumbPath(node.children ?? [], pageId, next);
    if (found) return found;
  }
  return null;
}

function truncateLabel(value: string, maxChars: number): string {
  const trimmed = value.trim();
  if (trimmed.length <= maxChars) return trimmed;
  return `${trimmed.slice(0, Math.max(0, maxChars - 1)).trimEnd()}…`;
}

export function DocumentationSpacePage() {
  const { spaceId = '', pageId } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const shell = useDocumentationShell();
  const docsTreeWidthKey = shell.treeWidthKey;
  const docsTreeCollapsedKey = shell.treeCollapsedKey;
  const canWrite = shell.canWrite;
  const { confirm } = useConfirmDialog();

  const [space, setSpace] = useState<DocumentationSpace | null>(null);
  const [tree, setTree] = useState<DocumentationPageTreeNode[]>([]);
  const [page, setPage] = useState<DocumentationPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [createModalOpen, setCreateModalOpen] = useState(false);

  const [isEditing, setIsEditing] = useState(false);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [details, setDetails] = useState<DocumentationDetailsDraft>({
    visibility: 'Private',
    tagInput: '',
    previousPageId: '',
    nextPageId: '',
  });
  const [autosave, setAutosave] = useState<AutosaveIndicator>('idle');
  const [renamingId, setRenamingId] = useState<string | null>(null);
  const [moveTarget, setMoveTarget] = useState<{ id: string; title: string } | null>(null);
  const [publishTarget, setPublishTarget] = useState<{ id: string; title: string; expiresAtUtc?: string | null } | null>(
    null,
  );
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [versionsLoading, setVersionsLoading] = useState(false);
  const [versions, setVersions] = useState<DocumentationPageVersionSummary[]>([]);
  const {
    width: treeWidth,
    isResizing: isResizingTree,
    beginResize: beginTreeResize,
  } = useResizablePanelWidth(
    docsTreeWidthKey,
    DOCS_TREE_DEFAULT_WIDTH,
    DOCS_TREE_MIN_WIDTH,
    DOCS_TREE_MAX_WIDTH,
  );
  const [treeCollapsed, setTreeCollapsed] = useState(() => readTreeCollapsed(docsTreeCollapsedKey));

  function toggleTreeCollapsed(): void {
    setTreeCollapsed((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(docsTreeCollapsedKey, next ? '1' : '0');
      } catch {
        // ignore
      }
      return next;
    });
  }

  const dirtyRef = useRef(false);
  const skipAutosaveRef = useRef(false);
  const pageRef = useRef<DocumentationPage | null>(null);
  const draftRef = useRef({ title: '', body: '', details: details });
  const detailsSaveTimerRef = useRef<number | null>(null);

  pageRef.current = page;
  draftRef.current = { title, body, details };

  const flat = useMemo(() => flattenTree(tree), [tree]);
  const breadcrumb = useMemo(
    () => (page ? findBreadcrumbPath(tree, page.id) ?? [] : []),
    [tree, page],
  );

  const refreshTree = useCallback(async (): Promise<DocumentationPageTreeNode[]> => {
    const treeData = await getDocumentationPageTree(spaceId, true);
    setTree(treeData);
    return treeData;
  }, [spaceId]);

  const loadPage = useCallback(
    async (targetId: string, enterEdit = false): Promise<void> => {
      let pageData = await getDocumentationPage(spaceId, targetId);
      if (enterEdit && canWrite) {
        pageData = await beginEditDocumentationPage(spaceId, targetId);
      }
      setPage(pageData);
      setTitle(pageData.title);
      setBody(pageData.body ?? '');
      setDetails(detailsFromPage(pageData));
      dirtyRef.current = false;
      setAutosave('idle');
      skipAutosaveRef.current = true;
      if (enterEdit && canWrite) {
        setIsEditing(true);
      } else if (!enterEdit) {
        setIsEditing(false);
      }
    },
    [spaceId, canWrite],
  );

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      const showSpinner = !space || space.id !== spaceId;
      if (showSpinner) setLoading(true);
      try {
        if (
          dirtyRef.current &&
          isEditing &&
          pageRef.current &&
          pageId &&
          pageRef.current.id !== pageId
        ) {
          await savePage('Draft', { silent: true });
        }

        const [spaceData, treeData] = await Promise.all([
          getDocumentationSpace(spaceId),
          getDocumentationPageTree(spaceId, true),
        ]);
        if (cancelled) return;

        setSpace(spaceData);
        setTree(treeData);

        const wantCreate = searchParams.get('create') === '1';
        const wantEdit = searchParams.get('edit') === '1';

        if (wantCreate && canWrite) {
          setSearchParams({}, { replace: true });
          setCreateModalOpen(true);
          return;
        }

        const targetId = pageId;
        if (targetId) {
          await loadPage(targetId, wantEdit);
          if (wantEdit) {
            setSearchParams({}, { replace: true });
          }
        } else {
          setPage(null);
          setIsEditing(false);
        }
      } catch {
        if (!cancelled) toast.error('Falha ao carregar o documento.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload on space/page navigation
  }, [spaceId, pageId]);

  function openCreatePageModal(): void {
    if (!canWrite || creating) return;
    setCreateModalOpen(true);
  }

  async function handleCreatePage(contentFormat: DocumentationContentFormat): Promise<void> {
    if (!canWrite) return;
    setCreateModalOpen(false);
    setCreating(true);
    try {
      if (dirtyRef.current && isEditing) {
        await savePage('Draft', { silent: true });
      }
      const parentPageId = page?.id ?? null;
      const created = await createDocumentationPage(spaceId, {
        title: 'Nova página',
        body: defaultBodyForFormat(contentFormat),
        contentFormat,
        status: 'Draft',
        visibility: 'Private',
        parentPageId,
      });
      await refreshTree();
      toast.success('Página criada.');
      navigate(`${shell.basePath}/${spaceId}/pages/${created.id}?edit=1`);
    } catch {
      toast.error('Falha ao criar página.');
    } finally {
      setCreating(false);
    }
  }

  function markDirty(): void {
    if (skipAutosaveRef.current) {
      skipAutosaveRef.current = false;
      return;
    }
    dirtyRef.current = true;
    setAutosave('dirty');
  }

  function buildUpsert(
    status: DocumentationPageStatus,
    overrides?: Partial<DocumentationPageUpsert>,
  ): DocumentationPageUpsert {
    const d = draftRef.current;
    return {
      title: d.title.trim() || 'Sem título',
      body: d.body,
      status,
      visibility: d.details.visibility,
      tagNames: parseTags(d.details.tagInput),
      previousPageId: d.details.previousPageId || null,
      nextPageId: d.details.nextPageId || null,
      parentPageId: pageRef.current?.parentPageId ?? null,
      sortOrder: pageRef.current?.sortOrder,
      ...overrides,
    };
  }

  const savePage = useCallback(
    async (
      status: DocumentationPageStatus,
      options?: { silent?: boolean; overrides?: Partial<DocumentationPageUpsert> },
    ): Promise<DocumentationPage | null> => {
      const current = pageRef.current;
      if (!canWrite || !current) return null;
      if (!draftRef.current.title.trim() && !options?.silent) {
        toast.error('Informe o título.');
        return null;
      }

      setAutosave('saving');
      try {
        const updated = await updateDocumentationPage(
          spaceId,
          current.id,
          buildUpsert(status, options?.overrides),
        );
        setPage(updated);
        setTitle(updated.title);
        setBody(updated.body ?? '');
        setDetails(detailsFromPage(updated));
        dirtyRef.current = false;
        skipAutosaveRef.current = true;
        setAutosave('saved');
        await refreshTree();
        if (!options?.silent) {
          toast.success(status === 'Published' ? 'Página publicada.' : 'Rascunho salvo.');
        }
        return updated;
      } catch {
        setAutosave('error');
        if (!options?.silent) toast.error('Falha ao salvar.');
        return null;
      }
    },
    [canWrite, spaceId, refreshTree],
  );

  const saveDetailsMetadata = useCallback(
    async (draft: DocumentationDetailsDraft): Promise<boolean> => {
      const current = pageRef.current;
      if (!canWrite || !current) return false;
      try {
        const updated = await updateDocumentationPageMetadata(spaceId, current.id, {
          visibility: draft.visibility,
          tagNames: parseTags(draft.tagInput),
          previousPageId: draft.previousPageId || null,
          nextPageId: draft.nextPageId || null,
          categoryId: current.categoryId ?? null,
        });
        setPage(updated);
        setDetails(detailsFromPage(updated));
        return true;
      } catch {
        toast.error('Falha ao salvar detalhes.');
        if (pageRef.current) {
          setDetails(detailsFromPage(pageRef.current));
        }
        return false;
      }
    },
    [canWrite, spaceId],
  );

  function scheduleDetailsSave(draft: DocumentationDetailsDraft): void {
    if (detailsSaveTimerRef.current != null) {
      window.clearTimeout(detailsSaveTimerRef.current);
    }
    detailsSaveTimerRef.current = window.setTimeout(() => {
      detailsSaveTimerRef.current = null;
      void saveDetailsMetadata(draft);
    }, 400);
  }

  useEffect(() => {
    return () => {
      if (detailsSaveTimerRef.current != null) {
        window.clearTimeout(detailsSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!isEditing || !canWrite) return;
    if (autosave !== 'dirty') return;

    const timer = window.setTimeout(() => {
      void savePage('Draft', { silent: true });
    }, AUTOSAVE_MS);

    return () => window.clearTimeout(timer);
  }, [isEditing, canWrite, autosave, title, body, details, savePage]);

  async function flushIfDirty(): Promise<void> {
    if (!dirtyRef.current || !isEditing) return;
    await savePage('Draft', { silent: true });
  }

  function enterEdit(): void {
    if (!canWrite || !page) return;
    void (async () => {
      try {
        const pageData = await beginEditDocumentationPage(spaceId, page.id);
        setPage(pageData);
        setTitle(pageData.title);
        setBody(pageData.body ?? '');
        setDetails(detailsFromPage(pageData));
        dirtyRef.current = false;
        skipAutosaveRef.current = true;
        setIsEditing(true);
        setAutosave('idle');
      } catch {
        toast.error('Falha ao iniciar edição.');
      }
    })();
  }

  async function handleCancel(): Promise<void> {
    if (!page) return;
    dirtyRef.current = false;
    skipAutosaveRef.current = true;
    try {
      const pageData = await getDocumentationPage(spaceId, page.id);
      setPage(pageData);
      setTitle(pageData.publishedTitle ?? pageData.title);
      setBody(pageData.publishedBody ?? pageData.body ?? '');
      setDetails(detailsFromPage(pageData));
    } catch {
      setTitle(page.publishedTitle ?? page.title);
      setBody(page.publishedBody ?? page.body ?? '');
      setDetails(detailsFromPage(page));
    }
    setIsEditing(false);
    setAutosave('idle');
  }

  async function handleSaveDraft(): Promise<void> {
    await savePage('Draft');
  }

  async function handlePublish(): Promise<void> {
    if (!page) return;
    setPublishTarget({
      id: page.id,
      title: title || page.title,
      expiresAtUtc: page.expiresAtUtc,
    });
  }

  async function confirmPublish(expiresAtUtc: string | null): Promise<void> {
    const target = publishTarget;
    setPublishTarget(null);
    if (!target) return;

    try {
      if (target.id === page?.id && isEditing && dirtyRef.current) {
        await savePage('Draft', { silent: true });
      }

      const updated = await publishDocumentationPage(spaceId, target.id, { expiresAtUtc });
      await refreshTree();
      if (page?.id === target.id) {
        setPage(updated);
        setTitle(updated.title);
        setBody(updated.body ?? '');
        setDetails(detailsFromPage(updated));
        setIsEditing(false);
        dirtyRef.current = false;
        skipAutosaveRef.current = true;
        setAutosave('idle');
      }
      toast.success('Página publicada.');
    } catch {
      toast.error('Falha ao publicar.');
    }
  }

  async function openVersions(): Promise<void> {
    if (!page) return;
    setVersionsOpen(true);
    setVersionsLoading(true);
    try {
      const items = await listDocumentationPageVersions(spaceId, page.id);
      setVersions(items);
    } catch {
      toast.error('Falha ao carregar versões.');
      setVersions([]);
    } finally {
      setVersionsLoading(false);
    }
  }

  async function handleRestoreVersion(versionId: string): Promise<void> {
    if (!page) return;
    if (
      !(await confirm({
        title: 'Tornar versão atual',
        message:
          'Isso publica uma nova versão com o conteúdo selecionado e a define como atual. Continuar?',
        confirmLabel: 'Tornar atual',
      }))
    ) {
      return;
    }

    try {
      const updated = await restoreDocumentationPageVersion(spaceId, page.id, versionId);
      setPage(updated);
      setTitle(updated.publishedTitle ?? updated.title);
      setBody(updated.publishedBody ?? updated.body ?? '');
      setDetails(detailsFromPage(updated));
      setIsEditing(false);
      dirtyRef.current = false;
      skipAutosaveRef.current = true;
      setAutosave('idle');
      await refreshTree();
      const items = await listDocumentationPageVersions(spaceId, page.id);
      setVersions(items);
      toast.success(`Versão ${updated.publishedVersionLabel ?? ''} definida como atual.`);
    } catch {
      toast.error('Falha ao restaurar versão.');
    }
  }

  async function handleArchive(): Promise<void> {
    if (!page) return;
    const status = (page.status as DocumentationPageStatus) || 'Draft';
    if (status !== 'Published' && status !== 'Expired') {
      toast.info('Somente páginas publicadas ou expiradas podem ser arquivadas.');
      return;
    }
    if (
      !(await confirm({
        title: 'Arquivar página',
        message: `Arquivar “${page.title}”?`,
        confirmLabel: 'Arquivar',
        tone: 'danger',
      }))
    ) {
      return;
    }
    const updated = await savePage('Archived');
    if (updated) setIsEditing(false);
  }

  async function handleToggleVisibility(): Promise<void> {
    if (!canWrite || !page) return;
    const next: DocumentationVisibility = details.visibility === 'Public' ? 'Private' : 'Public';
    const draft: DocumentationDetailsDraft = { ...details, visibility: next };
    setDetails(draft);
    const saved = await saveDetailsMetadata(draft);
    if (saved) {
      toast.success(next === 'Public' ? 'Página pública.' : 'Página privada.');
    }
  }

  async function handleDuplicate(sourceId?: string): Promise<void> {
    if (!canWrite) return;
    const source =
      sourceId && sourceId !== page?.id
        ? await getDocumentationPage(spaceId, sourceId)
        : page;
    if (!source) return;
    try {
      const copy = await createDocumentationPage(spaceId, {
        title: `${source.title} (cópia)`,
        body: source.body,
        contentFormat: isMarkdownFormat(source.contentFormat) ? 'Markdown' : 'Html',
        status: 'Draft',
        visibility: (source.visibility as DocumentationVisibility) || 'Private',
        tagNames: source.tags ?? [],
        parentPageId: source.parentPageId ?? null,
      });
      await refreshTree();
      toast.success('Cópia criada.');
      navigate(`${shell.basePath}/${spaceId}/pages/${copy.id}?edit=1`);
    } catch {
      toast.error('Falha ao duplicar.');
    }
  }

  function handleExportPdf(): void {
    window.print();
  }

  async function applyMove(
    dragId: string,
    parentPageId: string | null,
    sortOrder: number,
  ): Promise<void> {
    try {
      const current = await getDocumentationPage(spaceId, dragId);
      await updateDocumentationPageMetadata(spaceId, dragId, {
        visibility: (current.visibility as DocumentationVisibility) || 'Private',
        tagNames: current.tags ?? [],
        previousPageId: current.previousPageId ?? null,
        nextPageId: current.nextPageId ?? null,
        categoryId: current.categoryId ?? null,
        parentPageId,
        sortOrder,
      });
      await refreshTree();
      if (page?.id === dragId) {
        await loadPage(dragId, isEditing);
      }
    } catch {
      toast.error('Falha ao mover página.');
    }
  }

  async function reindexSiblings(
    ordered: DocumentationPageTreeNode[],
    parentPageId: string | null,
  ): Promise<void> {
    for (let index = 0; index < ordered.length; index += 1) {
      const node = ordered[index];
      const current =
        node.id === page?.id && page ? page : await getDocumentationPage(spaceId, node.id);
      await updateDocumentationPageMetadata(spaceId, node.id, {
        visibility: (current.visibility as DocumentationVisibility) || 'Private',
        tagNames: current.tags ?? [],
        previousPageId: current.previousPageId ?? null,
        nextPageId: current.nextPageId ?? null,
        categoryId: current.categoryId ?? null,
        parentPageId,
        sortOrder: index,
      });
    }
  }

  async function reorderSibling(nodeId: string, delta: number): Promise<void> {
    if (!canWrite) return;
    await flushIfDirty();
    const parentPageId = getParentId(tree, nodeId);
    const siblings = getSiblings(tree, parentPageId);
    const index = siblings.findIndex((node) => node.id === nodeId);
    const nextIndex = index + delta;
    if (index < 0 || nextIndex < 0 || nextIndex >= siblings.length) {
      return;
    }

    const ordered = [...siblings];
    const [moved] = ordered.splice(index, 1);
    ordered.splice(nextIndex, 0, moved);

    try {
      await reindexSiblings(ordered, parentPageId);
      await refreshTree();
      if (page?.id === nodeId) {
        await loadPage(nodeId, isEditing);
      }
    } catch {
      toast.error('Falha ao reordenar página.');
    }
  }

  async function handleTreeDrop(
    dragId: string,
    targetId: string,
    position: TreeDropPosition,
  ): Promise<void> {
    await flushIfDirty();
    const dragged = findNode(tree, dragId);
    if (!dragged) return;

    if (targetId === DOCUMENTATION_TREE_ROOT_ID) {
      const siblings = tree.filter((s) => s.id !== dragId);
      const ordered = [...siblings, dragged];
      try {
        await reindexSiblings(ordered, null);
        await refreshTree();
        if (page?.id === dragId) {
          await loadPage(dragId, isEditing);
        }
      } catch {
        toast.error('Falha ao mover página.');
      }
      return;
    }

    let newParentId: string | null;
    let ordered: DocumentationPageTreeNode[];

    if (position === 'into') {
      newParentId = targetId;
      ordered = [...getSiblings(tree, targetId).filter((s) => s.id !== dragId), dragged];
    } else {
      newParentId = getParentId(tree, targetId);
      const siblings = getSiblings(tree, newParentId).filter((s) => s.id !== dragId);
      const targetIndex = siblings.findIndex((s) => s.id === targetId);
      if (targetIndex < 0) return;
      const insertIndex = position === 'before' ? targetIndex : targetIndex + 1;
      ordered = [...siblings];
      ordered.splice(insertIndex, 0, dragged);
    }

    try {
      await reindexSiblings(ordered, newParentId);
      await refreshTree();
      if (page?.id === dragId) {
        await loadPage(dragId, isEditing);
      }
    } catch {
      toast.error('Falha ao mover página.');
    }
  }

  async function handleRename(nodeId: string, newTitle: string): Promise<void> {
    setRenamingId(null);
    try {
      const current = nodeId === page?.id ? page : await getDocumentationPage(spaceId, nodeId);
      if (!current) return;
      await updateDocumentationPage(spaceId, nodeId, {
        title: newTitle,
        body: current.body,
        status: (current.status as DocumentationPageStatus) || 'Draft',
        visibility: (current.visibility as DocumentationVisibility) || 'Private',
        tagNames: current.tags ?? [],
        previousPageId: current.previousPage?.id ?? null,
        nextPageId: current.nextPage?.id ?? null,
        parentPageId: current.parentPageId ?? null,
        sortOrder: current.sortOrder,
      });
      await refreshTree();
      if (page?.id === nodeId) {
        setTitle(newTitle);
        setPage({ ...current, title: newTitle });
      }
      toast.success('Título atualizado.');
    } catch {
      toast.error('Falha ao renomear.');
    }
  }

  async function handleMakeRoot(nodeId: string): Promise<void> {
    await flushIfDirty();
    const siblings = tree.filter((s) => s.id !== nodeId);
    const sortOrder =
      siblings.length === 0 ? 0 : Math.max(...siblings.map((s) => s.sortOrder)) + 1;
    await applyMove(nodeId, null, sortOrder);
    toast.success('Página movida para a raiz.');
  }

  async function handleDeletePage(nodeId: string): Promise<void> {
    const node = findNode(tree, nodeId);
    if (
      !(await confirm({
        title: 'Excluir página',
        message: `Excluir “${node?.title ?? 'esta página'}”?`,
        confirmLabel: 'Excluir',
        tone: 'danger',
      }))
    ) {
      return;
    }
    try {
      await deleteDocumentationPage(spaceId, nodeId);
      toast.success('Página excluída.');
      const nextTree = await refreshTree();
      if (page?.id === nodeId) {
        setIsEditing(false);
        const nextId = flattenTree(nextTree)[0]?.id;
        if (nextId) {
          navigate(`${shell.basePath}/${spaceId}/pages/${nextId}`);
        } else {
          setPage(null);
          navigate(`${shell.basePath}/${spaceId}`);
        }
      }
    } catch {
      toast.error('Falha ao excluir.');
    }
  }

  async function setStatusForNode(
    nodeId: string,
    status: DocumentationPageStatus,
    options?: { expiresAtUtc?: string | null },
  ): Promise<void> {
    await flushIfDirty();
    if (status === 'Archived') {
      const currentStatus =
        nodeId === page?.id
          ? ((page.status as DocumentationPageStatus) || 'Draft')
          : ((findNode(tree, nodeId)?.status as DocumentationPageStatus) || 'Draft');
      if (currentStatus !== 'Published' && currentStatus !== 'Expired') {
        toast.info('Somente páginas publicadas ou expiradas podem ser arquivadas.');
        return;
      }
    }

    if (status === 'Published') {
      try {
        if (nodeId === page?.id && isEditing && dirtyRef.current) {
          await savePage('Draft', { silent: true });
        }
        const updated = await publishDocumentationPage(spaceId, nodeId, {
          expiresAtUtc: options?.expiresAtUtc ?? null,
        });
        await refreshTree();
        if (page?.id === nodeId) {
          setPage(updated);
          setTitle(updated.title);
          setBody(updated.body ?? '');
          setDetails(detailsFromPage(updated));
          setIsEditing(false);
          dirtyRef.current = false;
          skipAutosaveRef.current = true;
          setAutosave('idle');
        }
        toast.success('Página publicada.');
      } catch {
        toast.error('Falha ao publicar.');
      }
      return;
    }

    if (nodeId === page?.id && isEditing) {
      await savePage(status);
      if (status === 'Archived') setIsEditing(false);
      return;
    }
    try {
      const current = await getDocumentationPage(spaceId, nodeId);
      await updateDocumentationPage(spaceId, nodeId, {
        title: current.title,
        body: current.body,
        status,
        visibility: (current.visibility as DocumentationVisibility) || 'Private',
        tagNames: current.tags ?? [],
        previousPageId: current.previousPage?.id ?? null,
        nextPageId: current.nextPage?.id ?? null,
        parentPageId: current.parentPageId ?? null,
        sortOrder: current.sortOrder,
        expiresAtUtc: current.expiresAtUtc,
      });
      await refreshTree();
      if (page?.id === nodeId) await loadPage(nodeId, false);
      toast.success(status === 'Archived' ? 'Página arquivada.' : 'Salvo.');
    } catch {
      toast.error('Falha ao atualizar status.');
    }
  }

  async function handleMenuAction(nodeId: string, action: DocumentationTreeMenuAction): Promise<void> {
    switch (action) {
      case 'rename':
        setRenamingId(nodeId);
        break;
      case 'move': {
        const node = findNode(tree, nodeId);
        if (node) setMoveTarget({ id: node.id, title: node.title });
        break;
      }
      case 'moveUp':
        await reorderSibling(nodeId, -1);
        break;
      case 'moveDown':
        await reorderSibling(nodeId, 1);
        break;
      case 'makeRoot':
        await handleMakeRoot(nodeId);
        break;
      case 'duplicate':
        await handleDuplicate(nodeId);
        break;
      case 'save':
        if (nodeId === page?.id && isEditing) await handleSaveDraft();
        else toast.info('Abra a página em edição para salvar o conteúdo.');
        break;
      case 'edit':
        if (nodeId === page?.id) {
          enterEdit();
        } else {
          await flushIfDirty();
          navigate(`${shell.basePath}/${spaceId}/pages/${nodeId}?edit=1`);
        }
        break;
      case 'view': {
        if (!space?.shareToken) {
          toast.info('O documento precisa estar público para visualizar no modo live.');
          break;
        }
        const node = findNode(tree, nodeId);
        const slug = (nodeId === page?.id ? page?.slug : null) ?? node?.slug;
        if (!slug) {
          toast.error('Não foi possível abrir a visualização.');
          break;
        }
        window.open(
          `/docs/${space.shareToken}/${encodeURIComponent(slug)}?countView=false`,
          '_blank',
          'noopener,noreferrer',
        );
        break;
      }
      case 'publish': {
        const node = findNode(tree, nodeId);
        if (!node) break;
        if (nodeId === page?.id) {
          setPublishTarget({
            id: node.id,
            title: node.title,
            expiresAtUtc: page.expiresAtUtc,
          });
          break;
        }
        void (async () => {
          try {
            const current = await getDocumentationPage(spaceId, nodeId);
            setPublishTarget({
              id: node.id,
              title: node.title,
              expiresAtUtc: current.expiresAtUtc,
            });
          } catch {
            setPublishTarget({ id: node.id, title: node.title });
          }
        })();
        break;
      }
      case 'archive':
        await setStatusForNode(nodeId, 'Archived');
        break;
      case 'delete':
        await handleDeletePage(nodeId);
        break;
      default:
        break;
    }
  }

  if (loading) {
    return <LoadingSpinner label="Carregando documento..." />;
  }

  if (!space) {
    return <p className="erp-page-description p-6">Documento não encontrado.</p>;
  }

  return (
    <div
      className={`docs-wiki-layout${detailsOpen ? ' docs-wiki-layout--details' : ''}${
        isResizingTree ? ' is-resizing' : ''
      }${treeCollapsed ? ' docs-wiki-layout--tree-collapsed' : ''}`}
      style={{
        gridTemplateColumns: `${treeCollapsed ? DOCS_TREE_COLLAPSED_WIDTH : treeWidth}px minmax(0, 1fr)`,
      }}
    >
      <aside className="docs-wiki-sidebar docs-print-hide">
        {treeCollapsed ? (
          <div className="docs-wiki-sidebar__collapsed">
            <button
              type="button"
              className="docs-wiki-sidebar__expand-btn"
              title="Expandir árvore de documentos"
              aria-label="Expandir árvore de documentos"
              onClick={toggleTreeCollapsed}
            >
              <Bars3Icon className="h-5 w-5" />
            </button>
          </div>
        ) : (
          <>
            <div className="docs-wiki-sidebar__header">
              <div className="docs-wiki-sidebar__header-row">
                <Link to={shell.basePath} className="erp-link-back">
                  ← Documentos
                </Link>
                <button
                  type="button"
                  className="docs-icon-btn"
                  title="Recolher árvore"
                  aria-label="Recolher árvore de documentos"
                  onClick={toggleTreeCollapsed}
                >
                  <ChevronDoubleLeftIcon className="h-4 w-4" />
                </button>
              </div>
              {canWrite ? (
                <button
                  type="button"
                  className="erp-btn-secondary w-full text-sm"
                  disabled={creating}
                  onClick={openCreatePageModal}
                >
                  {creating ? 'Criando...' : '+ Nova página'}
                </button>
              ) : null}
            </div>
            <div className="docs-wiki-sidebar__body">
              <DocumentationPageTree
                nodes={tree}
                activeId={page?.id}
                rootActive={!page}
                spaceId={spaceId}
                spaceName={space.name}
                canWrite={canWrite}
                onDrop={(dragId, targetId, position) => void handleTreeDrop(dragId, targetId, position)}
                onMenuAction={(id, action) => void handleMenuAction(id, action)}
                onRename={(id, newTitle) => void handleRename(id, newTitle)}
                renamingId={renamingId}
                onCancelRename={() => setRenamingId(null)}
              />
            </div>
            <ColumnResizeHandle
              onResizeStart={beginTreeResize}
              label="Redimensionar árvore de documentos"
            />
          </>
        )}
      </aside>

      <main className="docs-wiki-main">
        {!page ? (
          <div className="docs-wiki-main__scroll">
            <div className="docs-wiki-empty">
              <h1 className="erp-page-title">{space.name}</h1>
              <p className="erp-page-description">
                {space.description ||
                  (tree.length === 0
                    ? 'Este documento ainda não tem páginas. Crie a primeira para começar.'
                    : 'Nenhuma página selecionada. Escolha uma página na árvore ou crie uma nova.')}
              </p>
              {canWrite ? (
                <button type="button" className="erp-btn-primary" onClick={openCreatePageModal}>
                  {tree.length === 0 ? 'Criar primeira página' : '+ Nova página'}
                </button>
              ) : null}
            </div>
          </div>
        ) : (
          <>
            <div className="docs-wiki-main__chrome docs-print-hide">
              <nav className="docs-breadcrumb" aria-label="Caminho do documento">
                <Link
                  to={`${shell.basePath}/${spaceId}`}
                  className="docs-breadcrumb__link"
                  title={space.name}
                  onClick={() => void flushIfDirty()}
                >
                  {truncateLabel(space.name, 20)}
                </Link>
                {breadcrumb.map((crumb, index) => {
                  const isLast = index === breadcrumb.length - 1;
                  return (
                    <span key={crumb.id} className="docs-breadcrumb__item">
                      <span className="docs-breadcrumb__sep" aria-hidden>
                        /
                      </span>
                      {isLast ? (
                        <span className="docs-breadcrumb__current" aria-current="page">
                          {isEditing ? title || crumb.title : crumb.title}
                        </span>
                      ) : (
                        <Link
                          to={`${shell.basePath}/${spaceId}/pages/${crumb.id}`}
                          className="docs-breadcrumb__link"
                          onClick={() => void flushIfDirty()}
                        >
                          {crumb.title}
                        </Link>
                      )}
                    </span>
                  );
                })}
              </nav>

              <DocumentationPageToolbar
                canWrite={canWrite}
                isEditing={isEditing}
                status={page.status}
                isPublic={details.visibility === 'Public'}
                autosave={autosave}
                versionLabel={
                  isEditing
                    ? page.workingVersionLabel ?? page.publishedVersionLabel
                    : page.publishedVersionLabel ?? page.workingVersionLabel
                }
                hasUnpublishedChanges={Boolean(page.hasUnpublishedChanges)}
                liveViewHref={
                  page.status === 'Published' && space?.shareToken
                    ? `/docs/${space.shareToken}/${encodeURIComponent(page.slug)}?countView=false`
                    : null
                }
                detailsOpen={detailsOpen}
                onToggleDetails={() => setDetailsOpen((o) => !o)}
                onToggleVisibility={() => void handleToggleVisibility()}
                onEdit={enterEdit}
                onCancel={() => void handleCancel()}
                onSaveDraft={() => void handleSaveDraft()}
                onPublish={() => void handlePublish()}
                onArchive={() => void handleArchive()}
                onDuplicate={() => void handleDuplicate()}
                onExportPdf={handleExportPdf}
                onOpenVersions={() => void openVersions()}
              />
            </div>

            <div className="docs-wiki-main__scroll">
              <article className="docs-wiki-article" id="docs-print-root">
                {isEditing ? (
                  isMarkdownFormat(page.contentFormat) ? (
                    <DocumentationMarkdownEditor
                      value={body}
                      onChange={(next) => {
                        setBody(next);
                        markDirty();
                      }}
                      title={title}
                      onTitleChange={(next) => {
                        setTitle(next);
                        markDirty();
                      }}
                      pageId={page.id}
                    />
                  ) : (
                    <RichTextEditor
                      value={body}
                      onChange={(html) => {
                        setBody(html);
                        markDirty();
                      }}
                      title={title}
                      onTitleChange={(next) => {
                        setTitle(next);
                        markDirty();
                      }}
                      placeholder="Escreva a documentação..."
                      minHeight="360px"
                      allowLocalImages
                      pageId={page.id}
                      variant="documentation"
                    />
                  )
                ) : (
                  <>
                    <h1 className="docs-wiki-article__title">
                      {page.publishedTitle ?? page.title}
                    </h1>
                    {isMarkdownFormat(page.contentFormat) ? (
                      <div className="docs-wiki-prose">
                        <DocumentationMarkdownContent
                          value={page.publishedBody ?? page.body}
                          pageId={page.id}
                          emptyLabel="Sem conteúdo."
                        />
                      </div>
                    ) : (
                      <DocumentationHtmlContent
                        className="docs-wiki-prose"
                        html={page.publishedBody ?? page.body}
                        pageId={page.id}
                      />
                    )}
                  </>
                )}
              </article>

              {!isEditing ? (
                <nav className="docs-passadores docs-print-hide">
                  {page.previousPage ? (
                    <Link
                      className="docs-passador docs-passador--prev"
                      to={`${shell.basePath}/${spaceId}/pages/${page.previousPage.id}`}
                      onClick={() => void flushIfDirty()}
                    >
                      ← {page.previousPage.title}
                    </Link>
                  ) : (
                    <span />
                  )}
                  {page.nextPage ? (
                    <Link
                      className="docs-passador docs-passador--next"
                      to={`${shell.basePath}/${spaceId}/pages/${page.nextPage.id}`}
                      onClick={() => void flushIfDirty()}
                    >
                      {page.nextPage.title} →
                    </Link>
                  ) : flat.length > 1 ? (
                    <span className="text-sm text-secondary-400">Configure a próxima página em Detalhes</span>
                  ) : null}
                </nav>
              ) : null}
            </div>
          </>
        )}
      </main>

      {page ? (
        <DocumentationDetailsPanel
          open={detailsOpen}
          canWrite={canWrite}
          draft={details}
          pageOptions={flat}
          currentPageId={page.id}
          onClose={() => setDetailsOpen(false)}
          onChange={(patch) => {
            setDetails((d) => {
              const next = { ...d, ...patch };
              scheduleDetailsSave(next);
              return next;
            });
          }}
        />
      ) : null}

      {createModalOpen ? (
        <DocumentationCreatePageModal
          open
          onClose={() => setCreateModalOpen(false)}
          onConfirm={(contentFormat) => void handleCreatePage(contentFormat)}
        />
      ) : null}

      {moveTarget ? (
        <DocumentationMovePageModal
          open
          pageId={moveTarget.id}
          pageTitle={moveTarget.title}
          tree={tree}
          onClose={() => setMoveTarget(null)}
          onConfirm={(parentPageId) => {
            setMoveTarget(null);
            const siblings = getSiblings(tree, parentPageId).filter((s) => s.id !== moveTarget.id);
            const sortOrder =
              siblings.length === 0 ? 0 : Math.max(...siblings.map((s) => s.sortOrder)) + 1;
            void applyMove(moveTarget.id, parentPageId, sortOrder);
          }}
        />
      ) : null}

      {publishTarget ? (
        <DocumentationPublishModal
          open
          pageTitle={publishTarget.title}
          initialExpiresAtLocal={toDatetimeLocalValue(publishTarget.expiresAtUtc)}
          allowExpiration={shell.allowExpiration}
          onClose={() => setPublishTarget(null)}
          onConfirm={(expiresAtUtc) => void confirmPublish(expiresAtUtc)}
        />
      ) : null}

      {page ? (
        <DocumentationVersionsModal
          open={versionsOpen}
          pageTitle={page.title}
          versions={versions}
          loading={versionsLoading}
          canWrite={canWrite}
          onClose={() => setVersionsOpen(false)}
          onRestore={(versionId) => void handleRestoreVersion(versionId)}
        />
      ) : null}
    </div>
  );
}
